import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { xpApi, formatCents } from "@/lib/experienceApi";
import { formatParisDate, formatParisTime } from "@/lib/dtParis";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { Wine, Calendar, Ticket, Users, Heart, Trophy, Crown } from "lucide-react";

export default function ExperienceEventPublic() {
  const { slug } = useParams();
  const [data, setData] = useState(null);
  const [live, setLive] = useState(null);
  const [ttSelected, setTtSelected] = useState(null);
  const [slotSelected, setSlotSelected] = useState(null);
  const [qty, setQty] = useState(1);
  const [email, setEmail] = useState("");
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [teamId, setTeamId] = useState("");
  const [loading, setLoading] = useState(false);
  const nav = useNavigate();

  useEffect(() => {
    xpApi.publicEventDetail(slug).then((d) => {
      setData(d);
      if (d.ticket_types.length) setTtSelected(d.ticket_types[0].id);
    }).catch(() => { toast.error("Événement introuvable"); nav("/experience"); });
  }, [slug, nav]);

  useEffect(() => {
    const poll = () => xpApi.publicLiveLeaderboard(slug).then(setLive).catch(() => {});
    poll();
    const iv = setInterval(poll, 15000);
    return () => clearInterval(iv);
  }, [slug]);

  if (!data) return <div className="p-6 text-center text-sm text-neutral-500">Chargement…</div>;
  const { event, ticket_types, slots, organization, teams, top_favorites } = data;
  const tt = ticket_types.find(t => t.id === ttSelected);
  const teamsEnabled = (event.settings?.teams_enabled) && (teams?.length > 0);
  const showTop = Array.isArray(top_favorites) && top_favorites.length > 0;

  const buy = async () => {
    if (!tt) { toast.error("Choisissez un billet"); return; }
    if (!firstName.trim() || !lastName.trim()) { toast.error("Prénom et NOM requis"); return; }
    if (!email.trim()) { toast.error("Email requis"); return; }
    setLoading(true);
    try {
      const res = await xpApi.checkout({
        event_id: event.id,
        ticket_type_id: tt.id,
        slot_id: slotSelected,
        quantity: qty,
        customer_email: email.trim(),
        first_name: firstName.trim(),
        last_name: lastName.trim().toUpperCase(),
        team_id: teamId || null,
        origin_url: window.location.origin,
      });
      window.location.href = res.checkout_url;
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Paiement impossible");
    } finally { setLoading(false); }
  };

  return (
    <div className="max-w-3xl mx-auto px-4 py-8">
      <div className="rounded-2xl p-6 mb-6 text-white"
        style={{ background: `linear-gradient(135deg, ${event.branding?.primary_color || '#E5276A'} 0%, #1F3A5F 100%)` }}
      >
        <div className="text-xs uppercase tracking-widest opacity-80">{organization?.name}</div>
        <h1 className="text-3xl font-display mt-2">{event.name}</h1>
        <div className="text-sm mt-2 opacity-90 flex items-center gap-2">
          <Calendar className="w-4 h-4" />
          {formatParisDate(event.starts_at)} <span className="opacity-75">(heure de Paris)</span>
        </div>
        {event.description && <p className="mt-3 text-sm opacity-90">{event.description}</p>}
      </div>

      {showTop && (
        <Card className="mb-6 border-2 border-[#E5276A]/20 bg-gradient-to-br from-[#E5276A]/5 to-white"
              data-testid="top-favorites">
          <CardContent className="p-4">
            <div className="text-xs uppercase tracking-widest text-[#E5276A] font-semibold mb-3 flex items-center gap-1">
              <Heart className="w-3.5 h-3.5 fill-[#E5276A]" /> TOP 3 coups de cœur en direct
            </div>
            <div className="grid gap-2">
              {top_favorites.map((f, i) => (
                <div key={f.wine_event_id} className="flex items-center gap-3 py-1"
                     data-testid={`top-fav-${i}`}>
                  <span className="w-7 h-7 rounded-full bg-[#E5276A] text-white text-sm font-bold flex items-center justify-center">
                    {i + 1}
                  </span>
                  <span className="flex-1 font-semibold text-[#1F3A5F] truncate">{f.name}</span>
                  <span className="text-sm font-mono text-[#E5276A]">
                    <Heart className="w-3.5 h-3.5 inline mr-1 fill-[#E5276A]" />{f.votes}
                  </span>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {live?.game_active && (live.top_individual.length > 0 || live.top_teams.length > 0) && (
        <Card className="mb-6 border-2 border-yellow-400/50 bg-gradient-to-br from-yellow-50 to-white"
              data-testid="live-leaderboard">
          <CardContent className="p-4">
            <div className="flex items-center justify-between mb-3">
              <div className="text-xs uppercase tracking-widest text-amber-700 font-semibold flex items-center gap-1">
                <Trophy className="w-3.5 h-3.5" /> Palmarès live · le jeu est en cours
              </div>
              <span className="inline-flex items-center gap-1 text-[10px] text-red-500 font-semibold">
                <span className="w-2 h-2 rounded-full bg-red-500 animate-pulse" /> LIVE
              </span>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {live.top_individual.length > 0 && (
                <div>
                  <div className="text-[10px] uppercase text-neutral-500 mb-1">Meilleurs joueurs</div>
                  <div className="grid gap-1">
                    {live.top_individual.map((p, i) => (
                      <div key={i} className="flex items-center gap-2 text-sm" data-testid={`live-ind-${i}`}>
                        <span className={`w-5 h-5 rounded-full text-[10px] flex items-center justify-center font-bold text-white ${
                          i === 0 ? "bg-yellow-400" : i === 1 ? "bg-neutral-400" : i === 2 ? "bg-amber-700" : "bg-neutral-200"
                        }`}>{i + 1}</span>
                        <span className="flex-1 truncate">{p.display_name}</span>
                        <span className="text-[#E5276A] font-mono text-xs">{p.points}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              {live.top_teams.length > 0 && (
                <div>
                  <div className="text-[10px] uppercase text-neutral-500 mb-1">Meilleures équipes</div>
                  <div className="grid gap-1">
                    {live.top_teams.map((t, i) => (
                      <div key={i} className="flex items-center gap-2 text-sm" data-testid={`live-team-${i}`}>
                        <Crown className={`w-4 h-4 ${i === 0 ? "text-yellow-500 fill-yellow-300" : "text-neutral-400"}`} />
                        <span className="flex-1 truncate">{t.name}</span>
                        <span className="text-[#E5276A] font-mono text-xs">{t.points}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      )}

      <h2 className="text-lg font-semibold mb-3 flex items-center gap-2"><Ticket className="w-5 h-5 text-[#E5276A]" /> Choisissez votre formule</h2>
      <div className="grid gap-3 mb-6">
        {ticket_types.map((t) => (
          <Card key={t.id} data-testid={`tt-${t.id}`}
                className={`cursor-pointer transition ${ttSelected === t.id ? "border-[#E5276A] ring-2 ring-[#E5276A]" : ""}`}
                onClick={() => setTtSelected(t.id)}>
            <CardContent className="p-4 flex items-center justify-between">
              <div>
                <div className="font-semibold">{t.name}</div>
                {t.description && <div className="text-xs text-neutral-600 mt-1">{t.description}</div>}
                <div className="text-xs mt-1 flex items-center gap-1 text-neutral-500">
                  <Wine className="w-3.5 h-3.5" /> {t.tastings_included} dégustations incluses
                </div>
              </div>
              <div className="text-xl font-bold text-[#E5276A]">{formatCents(t.price_cents, t.currency)}</div>
            </CardContent>
          </Card>
        ))}
      </div>

      {slots.length > 0 && (
        <>
          <h3 className="font-semibold mb-2">
            Créneau <span className="text-xs font-normal text-neutral-500">(heure de Paris)</span>
          </h3>
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 mb-6">
            {slots.map(s => (
              <Button key={s.id} data-testid={`slot-${s.id}`}
                variant={slotSelected === s.id ? "default" : "outline"}
                onClick={() => setSlotSelected(s.id)}
                className={slotSelected === s.id ? "bg-[#E5276A] hover:bg-[#C01661]" : ""}>
                {formatParisTime(s.starts_at)}
                <span className="ml-1 text-xs opacity-70">({s.capacity - s.tickets_sold} places)</span>
              </Button>
            ))}
          </div>
        </>
      )}

      <div className="grid grid-cols-2 gap-2 mb-2">
        <Input data-testid="buyer-firstname" placeholder="Prénom" value={firstName}
               onChange={(e) => setFirstName(e.target.value)} />
        <Input data-testid="buyer-lastname" placeholder="NOM" value={lastName}
               onChange={(e) => setLastName(e.target.value)} />
      </div>
      <Input data-testid="buyer-email" type="email" placeholder="Email" value={email}
             onChange={(e) => setEmail(e.target.value)} className="mb-2" />

      {teamsEnabled && (
        <div className="mb-3">
          <label className="text-xs text-neutral-600 flex items-center gap-1 mb-1">
            <Users className="w-3.5 h-3.5" /> Équipe (optionnel)
          </label>
          <select data-testid="buyer-team" value={teamId}
                  onChange={(e) => setTeamId(e.target.value)}
                  className="w-full border rounded-md px-3 py-2 text-sm bg-white">
            <option value="">— Je rejoindrai plus tard —</option>
            {teams.map(t => (
              <option key={t.id} value={t.id}>{t.name}</option>
            ))}
          </select>
        </div>
      )}

      <div className="flex items-center gap-2 mb-4">
        <label className="text-sm">Quantité</label>
        <Input type="number" min={1} max={10} value={qty}
               onChange={(e) => setQty(parseInt(e.target.value) || 1)} className="w-20" />
      </div>

      <Button data-testid="checkout-btn" onClick={buy} disabled={loading} size="lg"
        className="w-full bg-[#E5276A] hover:bg-[#C01661]">
        {loading ? "Redirection…" : `Payer ${tt ? formatCents((tt.price_cents || 0) * qty, tt.currency) : ""}`}
      </Button>
      <p className="text-[11px] text-neutral-500 text-center mt-2">
        Paiement sécurisé Stripe. Vous recevrez votre code participant par email.
      </p>
    </div>
  );
}
